// ============================================================
// dadosDoNucleo.js — os números do núcleo, em um lugar só
//
// Saiu de dentro do Resumo quando a tela de início passou a precisar dos
// mesmos números. Duas telas calculando frequência por conta própria é
// como a planilha do programa chegou a ter três totais diferentes para a
// mesma coisa.
// ============================================================

import { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db, carregarDispensasDoMes, carregarNomesAtuais } from "./firebase";
import { calcularFrequencia, normalizarModalidade, mediaPctTotal } from "./calculo";

// Data de HOJE no fuso local, montada à mão.
//
// toISOString() converte para UTC e, à noite em Fortaleza, aponta para o dia
// seguinte. Os registros do dia deixariam de casar com os registros salvos,
// justamente no fim da tarde, que é quando a coordenação acompanha.
export function hojeLocalISO() {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, "0")}-${String(h.getDate()).padStart(2, "0")}`;
}

// Quem não é atleta é equipe, e não o contrário.
//
// A lista de perfis da equipe cresce: "Auxiliar de Preparação" e
// "Monitor Técnico" já apareceram depois de o sistema estar no ar.
// Perguntar pelo atleta, que é o perfil estável, faz o cadastro novo
// cair na equipe em vez de sumir no meio dos atletas de uma modalidade.
function ehDaEquipe(perfil) {
  const p = String(perfil || "").normalize("NFD")
    .replace(new RegExp("[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]", "g"), "")
    .toLowerCase();
  if (!p) return false;   // registro antigo sem perfil conta como atleta
  return !p.includes("atleta");
}

export function useDadosDoNucleo(nucleo) {
  const mesNum = new Date().getMonth();      // 0-11
  const ano = new Date().getFullYear();
  const mes = mesNum + 1;                     // 1-12
  const prefixo = `${ano}-${String(mes).padStart(2, "0")}`;
  const hojeISO = hojeLocalISO();

  const [dados, setDados] = useState({
    carregando: true,
    atletasAtivos: null,
    registrosHoje: null,
    registrosMes: null,
    pctMes: null,
    presentesHoje: [],     // [{ modalidade, nomes: [] }], com a equipe no fim
    equipeHoje: [],        // [{ nome, papel }]
  });

  useEffect(() => {
    let ativo = true;
    if (!nucleo) return undefined;
    setDados((d) => ({ ...d, carregando: true }));

    (async () => {
      let atletasAtivos = null;
      try {
        const q = query(collection(db, "users"), where("nucleo", "==", nucleo));
        const snap = await getDocs(q);
        atletasAtivos = snap.docs.filter((d) => {
          const u = d.data();
          return (u.perfil || "").toLowerCase().includes("atleta") && u.ativo !== false;
        }).length;
      } catch (e) { atletasAtivos = null; }

      // Uma consulta de presença serve para hoje, para o mês e para a média.
      let registrosHoje = null, registrosMes = null, pctMes = null, presentesHoje = [], equipeHoje = [];
      try {
        const q = query(collection(db, "attendance_logs"), where("nucleo", "==", nucleo));
        const snap = await getDocs(q);
        const todos = snap.docs.map((d) => d.data());

        let nHoje = 0, nMes = 0;
        const registrosMesArr = [];
        const hojePorMod = {};
        const equipe = new Map();   // nome -> papel
        todos.forEach((r) => {
          const dk = r.dateKey || (r.data ? r.data.split("/").reverse().join("-") : "");
          if (dk === hojeISO) {
            nHoje++;
            const nome = r.userName || r.email || "—";
            const mod = normalizarModalidade(r.modalidade) || "Sem modalidade";
            if (ehDaEquipe(r.perfil)) {
              // O papel mostrado é a modalidade em que a pessoa atua, que é o
              // que a coordenação usa para saber quem estava na pista. Para a
              // preparação física a modalidade já é "Preparação Física".
              if (!equipe.has(nome)) equipe.set(nome, mod);
            } else {
              if (!hojePorMod[mod]) hojePorMod[mod] = new Set();
              hojePorMod[mod].add(nome);
            }
          }
          if (dk.startsWith(prefixo)) { nMes++; registrosMesArr.push(r); }
        });

        try {
          // O mês vem do banco, como a presença: eram 450 dispensas para
          // usar as do mês.
          const dispensasMes = await carregarDispensasDoMes(nucleo, mes - 1, ano);
          const nomesAtuais = await carregarNomesAtuais(nucleo);
          const calc = calcularFrequencia({
            registros: registrosMesArr, dispensas: dispensasMes, mes: mesNum, ano, nomesAtuais,
          });
          const atletas = calc.membros.filter((m) => m.grupo === "ATLETA");
          const media = mediaPctTotal(atletas);
          if (media !== null) pctMes = Math.round(media * 100);
        } catch (e) { pctMes = null; }

        presentesHoje = Object.entries(hojePorMod)
          .sort(([a], [b]) => a.localeCompare(b, "pt-BR"))
          .map(([modalidade, nomes]) => ({
            modalidade,
            nomes: [...nomes].sort((a, b) => a.localeCompare(b, "pt-BR")),
          }));

        equipeHoje = [...equipe.entries()]
          .map(([nome, papel]) => ({ nome, papel }))
          .sort((x, y) => x.nome.localeCompare(y.nome, "pt-BR"));

        // A equipe entra no fim da lista por modalidade, como um grupo só.
        // Quem só quer a lista corrida continua tendo todo mundo.
        if (equipeHoje.length) {
          presentesHoje = [...presentesHoje, {
            modalidade: "Equipe técnica",
            nomes: equipeHoje.map((e) => e.nome),
          }];
        }

        registrosHoje = nHoje;
        registrosMes = nMes;
      } catch (e) {
        registrosHoje = null; registrosMes = null; pctMes = null; presentesHoje = []; equipeHoje = [];
      }

      if (ativo) {
        setDados({ carregando: false, atletasAtivos, registrosHoje, registrosMes, pctMes, presentesHoje, equipeHoje });
      }
    })();

    return () => { ativo = false; };
  }, [nucleo, prefixo, hojeISO, mesNum, ano, mes]);

  return { ...dados, mesNum, ano, hojeISO };
}
